import DOMPurify from 'dompurify';
import type { TextVariant } from "./types";

const HEADING_VARIANTS = new Set<TextVariant>([
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
]);

export function sanitizeEmailHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p',
      'br',
      'span',
      'strong',
      'b',
      'em',
      'i',
      'u',
      'a',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'ul',
      'ol',
      'li',
      's',
      'strike',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'style'],
  });
}

export function normalizeTextHtml(html: string): string {
  return sanitizeEmailHtml(html);
}

const LINK_STYLE_PROPS = /(?:^|;)\s*(?:color|text-decoration)\s*:[^;]*/gi;

function mergeLinkInlineStyle(
  existing: string | undefined,
  linkColor: string,
): string {
  const linkStyle = `color:${linkColor};text-decoration:underline`;
  if (!existing) {
    return linkStyle;
  }
  const cleaned = existing
    .replace(LINK_STYLE_PROPS, '')
    .replace(/^;+|;+$/g, '')
    .trim();
  return cleaned ? `${cleaned};${linkStyle}` : linkStyle;
}

function ensureExportLinkAttrs(attrs: string): string {
  let result = attrs;
  if (!/\btarget=/i.test(result)) {
    result = `${result} target="_blank"`;
  }
  if (!/\brel=/i.test(result)) {
    result = `${result} rel="noopener noreferrer"`;
  }
  return result;
}

export function textHtmlHasLinks(html: string): boolean {
  return /<a\b/i.test(html);
}

export function applyTextLinkStyles(html: string, linkColor: string): string {
  return html.replace(/<a\b([^>]*)>/gi, (_match, attrs: string) => {
    const exportAttrs = ensureExportLinkAttrs(attrs);
    const styleMatch = exportAttrs.match(/\bstyle="([^"]*)"/i);
    if (styleMatch) {
      const merged = mergeLinkInlineStyle(styleMatch[1], linkColor);
      return `<a${exportAttrs.replace(/\bstyle="[^"]*"/i, `style="${merged}"`)}>`;
    }
    const trimmedAttrs = exportAttrs.trimEnd();
    const spacer = trimmedAttrs.length > 0 ? `${trimmedAttrs} ` : '';
    return `<a ${spacer}style="${mergeLinkInlineStyle(undefined, linkColor)}">`;
  });
}

const BLOCKED_HREF_SCHEMES = /^(javascript|data|vbscript):/i;

export function sanitizeHref(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed || trimmed === '#') {
    return '#';
  }
  if (BLOCKED_HREF_SCHEMES.test(trimmed)) {
    return '#';
  }
  if (trimmed.startsWith('#') || trimmed.startsWith('/')) {
    return trimmed;
  }
  if (/^(https?:|mailto:|tel:)/i.test(trimmed)) {
    return trimmed;
  }
  if (trimmed.includes('{{')) {
    return trimmed;
  }
  return '#';
}

export function wrapTextInBlockHtml(html: string): string {
  const trimmed = html.trim();
  if (!trimmed) {
    return '<p></p>';
  }
  if (/<(p|div|h[1-6]|ul|ol)[\s>]/i.test(trimmed)) {
    return trimmed;
  }
  return `<p>${trimmed}</p>`;
}

export function wrapTextVariantHtml(
  html: string,
  variant: TextVariant,
): string {
  if (variant === 'normal' || !HEADING_VARIANTS.has(variant)) {
    return html;
  }
  const trimmed = html.trim();
  if (!trimmed) {
    return `<${variant}></${variant}>`;
  }
  const headingMatch = trimmed.match(/^<(h[1-6])\b[^>]*>([\s\S]*)<\/\1>$/i);
  if (headingMatch) {
    return trimmed;
  }
  const paragraphMatch = trimmed.match(/^<p\b[^>]*>([\s\S]*)<\/p>$/i);
  const inner = paragraphMatch ? paragraphMatch[1] : trimmed;
  return `<${variant}>${inner}</${variant}>`;
}
